import { useState, useEffect } from 'react';
import { sendBookingToTelegram } from '../utils/telegram';
import { useGeo, COUNTRY_NAMES } from '../context/GeoContext';
import { useTheme } from '../context/ThemeContext';
import { useSmartCaptcha } from '../hooks/useSmartCaptcha';
import '../styles/modal.css';

const INITIAL = {
  firstName: '', lastName: '', phone: '',
  email: '', country: '', comment: '',
  contactPref: 'email', selectedDate: '',
};

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isValidPhone = (v) => /^[+]?[\d\s\-().]{7,20}$/.test(v.trim());

const BookingModal = ({ isOpen, onClose, card, preselectedDate }) => {
  const geoCountry = useGeo();
  const { dark } = useTheme();
  const [form, setForm]     = useState(INITIAL);
  const { containerRef: captchaRef, token: captchaToken, reset: resetCaptcha } = useSmartCaptcha(isOpen, dark ? 'dark' : 'light');

  useEffect(() => {
    if (geoCountry && !form.country) {
      setForm(f => ({ ...f, country: COUNTRY_NAMES[geoCountry] || '' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoCountry]);
  const [status, setStatus] = useState('idle');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [consent, setConsent] = useState(false);

  const availableDates = card?.availableDates || [];
  const hasDateChoice  = !preselectedDate && card?._type === 'study' && availableDates.length > 0;

  const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const fixedDateStart = preselectedDate || formatDate(card?.startDate);
  const fixedDateEnd   = !preselectedDate ? formatDate(card?.endDate) : null;
  const fixedDate      = fixedDateStart && fixedDateEnd
    ? `с ${fixedDateStart} по ${fixedDateEnd}`
    : fixedDateStart;

  const typeTitle = {
    study:      'Запись на курс',
    adventures: 'Заявка на путешествие',
    adventure:  'Заявка на путешествие',
    races:      'Заявка на регату',
    race:       'Заявка на регату',
  }[card?._type] || 'Оставить заявку';

  const change = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'email') setEmailError('');
    if (name === 'phone') setPhoneError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidPhone(form.phone)) {
      setPhoneError('Введите корректный номер телефона (например: +7 900 000 00 00)');
      return;
    }

    if (!form.email) {
      setEmailError('Email обязателен для связи с вами');
      return;
    }
    if (!isValidEmail(form.email)) {
      setEmailError('Введите корректный email (например: ivan@example.com)');
      return;
    }

    if (!captchaToken) {
      setStatus('error');
      return;
    }

    setStatus('sending');
    try {
      await sendBookingToTelegram({
        cardTitle:        card?.title,
        cardType:         card?._type,
        selectedDate:     hasDateChoice ? form.selectedDate : fixedDate,
        firstName:        form.firstName,
        lastName:         form.lastName,
        phone:            form.phone,
        email:            form.email || null,
        country:          form.country,
        comment:          form.comment,
        contactPref:      form.contactPref,
        captchaToken,
      });
      // Письмо-подтверждение посетителю отправляет сервер сам (см. /api/booking) —
      // отдельный запрос отсюда раньше падал с 401 (у анонимных посетителей нет токена авторизации).
      setStatus('done');
    } catch {
      setStatus('error');
      resetCaptcha();
    }
  };

  const handleClose = () => {
    setForm(INITIAL);
    setStatus('idle');
    setEmailError('');
    setPhoneError('');
    setConsent(false);
    resetCaptcha();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>×</button>

        {status === 'done' ? (
          <div className="modal-success">
            <div className="modal-success-icon">✅</div>
            <h2>Заявка отправлена!</h2>
            <p>Мы свяжемся с вами в ближайшее время.</p>
            <button className="submit-btn" onClick={handleClose}>Закрыть</button>
          </div>
        ) : (
          <>
            <h2>{typeTitle}</h2>
            {card?.title && <div className="modal-event-name">{card.title}</div>}

            <form onSubmit={handleSubmit}>
              {hasDateChoice && (
                <div className="form-group">
                  <label>Выберите дату <em>*</em></label>
                  <div className="modal-dates">
                    {availableDates.map((slot, i) => {
                      const label = typeof slot === 'string' ? slot : slot.isoDate
                        ? (() => { const [y,m,d] = slot.isoDate.split('-').map(Number);
                            const MG = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
                            return `${d} ${MG[m-1]} ${y}`; })()
                        : String(slot);
                      const spaces = typeof slot === 'object' ? slot.spaces : null;
                      const full   = spaces !== null && spaces <= 0;
                      return (
                        <label key={i} className={`modal-date-option ${form.selectedDate === label ? 'selected' : ''} ${full ? 'modal-date-full' : ''}`}>
                          <input
                            type="radio" name="selectedDate" value={label}
                            checked={form.selectedDate === label}
                            onChange={change}
                            disabled={full}
                            required
                          />
                          <span>{label}</span>
                          {spaces !== null && (
                            <span className={`modal-date-spaces ${full ? 'full' : ''}`}>
                              {full ? 'Мест нет' : `${spaces} мест`}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {fixedDate && !hasDateChoice && (
                <div className="modal-fixed-date">📅 {fixedDate}</div>
              )}

              <div className="form-row-2">
                <div className="form-group">
                  <label>Имя <em>*</em></label>
                  <input name="firstName" value={form.firstName} onChange={change} placeholder="Иван" required />
                </div>
                <div className="form-group">
                  <label>Фамилия <em>*</em></label>
                  <input name="lastName" value={form.lastName} onChange={change} placeholder="Иванов" required />
                </div>
              </div>

              <div className="form-group">
                <label>Телефон <em>*</em></label>
                <input
                  name="phone" type="tel" value={form.phone} onChange={change}
                  placeholder="+7 900 000 00 00" required
                  className={phoneError ? 'input-error' : ''}
                />
                {phoneError && <p className="modal-field-error">{phoneError}</p>}
              </div>

              <div className="form-group">
                <label>Email <span style={{ color: '#e53935', fontSize: '0.85em' }}>*</span></label>
                <input
                  name="email"
                  type="text"
                  value={form.email}
                  onChange={change}
                  placeholder="ivan@example.com"
                  className={emailError ? 'input-error' : ''}
                />
                {emailError && <p className="modal-field-error">{emailError}</p>}
              </div>

              <div className="form-group">
                <label>Страна проживания</label>
                <input name="country" value={form.country} onChange={change} placeholder="Россия" />
              </div>

              <div className="form-group">
                <label>Предпочтительный способ ответа <em>*</em></label>
                <div className="modal-contact-pref">
                  <label className={`modal-pref-option ${form.contactPref === 'email' ? 'selected' : ''}`}>
                    <input type="radio" name="contactPref" value="email"
                      checked={form.contactPref === 'email'} onChange={change} />
                    📧 Email
                  </label>
                  <label className={`modal-pref-option ${form.contactPref === 'telegram' ? 'selected' : ''}`}>
                    <input type="radio" name="contactPref" value="telegram"
                      checked={form.contactPref === 'telegram'} onChange={change} />
                    ✈️ Telegram
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Комментарий</label>
                <textarea name="comment" value={form.comment} onChange={change} rows={3}
                  placeholder="Опыт, пожелания, вопросы..." />
              </div>

              {status === 'error' && (
                <p className="modal-error">Ошибка отправки. Проверьте интернет и попробуйте ещё раз.</p>
              )}

              <label className="modal-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                />
                <span>
                  Я согласен(а) с{' '}
                  <a href="/personal-data" target="_blank" rel="noopener noreferrer">политикой обработки персональных данных</a>
                  {' '}и{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">политикой конфиденциальности</a>
                </span>
              </label>

              <div ref={captchaRef} className="captcha-wrap" />

              <button type="submit" className="submit-btn" disabled={status === 'sending' || !consent || !captchaToken}>
                {status === 'sending' ? 'Отправляем...' : 'Отправить заявку'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
